import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../store";
import {
  Button,
  Table,
  TableTR,
  FilterValue,
  Filter,
  toast,
} from "../../../components";
import { Avatar } from "@/utils/ui/Avatar/Avatar";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import AddBusinessForm from "@/components/Business/AddBusinessForm";
import {
  archiveBusiness,
  getBusiness,
  getListBusiness,
  getListOrganization,
} from "@/store/constants/constants.action";
import { useAppDispatch } from "../../../store";
import {
  setCurrentBusiness,
  setBusinessList,
} from "@/store/constants/constants.slice";
import { formatTimestampToAgo } from "../../../utils";
import { Option } from "@/utils/utilFunctions/filters";
import Modal from "@/components/Modal/Modal";
import PageHeader from "@/components/Layout/PageHeader";
import { Edit } from "lucide-react";
import { NoContentFound } from "@/utils/ui/NoContent";

const headers: { name: string; width: string }[] = [
  { name: "Name", width: "40%" },
  { name: "Created On", width: "30%" },
  { name: "Status", width: "20%" },
  { name: "Action", width: "10%" },
];

const BusinessList = ({
  businessList,
  getListBusiness,
  setCurrentBusiness,
  setBusinessList,
  isClearFilter,
  loading,
  count,

  bunchBusinessList,
  getBusiness,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);

  const [formState, setFormState] = useState<{
    isDirty: boolean;
    isValid: boolean;
    isLoading: { error: boolean; success: boolean; loading: boolean };
  }>({
    isDirty: false,
    isValid: false,
    isLoading: { error: false, success: false, loading: false },
  });
  const [filters, setFilters] = useState<Record<string, FilterValue>>({});
  const [availableFilters, setAvailableFilters] = useState<Filter[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const handleModalOpen = () => {
    dispatch(setCurrentBusiness(null));
    setSelectedBusiness(null);
    setIsModalOpen(true);
  };

  const handleSetFilters = (filters: Record<string, FilterValue>) => {
    setFilters(filters);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBusiness(null);
  };
  useEffect(() => {
    setBusinessList({
      data: { list: [], count: 0, isLoading: true, error: "" },
    });
  }, [setBusinessList]);

  const handleArchiveToggle = (item: any) => {
    if (archivingId === item.id) return;
    setArchivingId(item.id);
    const newArchive = item.archive === 1 ? 0 : 1;
    dispatch(
      archiveBusiness(
        { id: item.id, archive: newArchive },
        () => {
          toast.success(
            newArchive === 1
              ? "Business marked as Inactive"
              : "Business marked as Active"
          );
          setArchivingId(null);
        },
        () => setArchivingId(null)
      )
    );
  };

  const handleEdit = async (business: any) => {
    // dispatch(setCurrentBusiness(business));

    const result = await getBusiness({ id: business.id });

    if (result.success) {
      setSelectedBusiness(business);
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (bunchBusinessList?.length === 0 || availableFilters?.length > 0) return;
    const businessListOptionArray: Option[] = bunchBusinessList?.map(
      (values: any) => ({
        label: formatEntityName(values),
        value: values?.id,
        displayValue: formatEntityName(values),
        baseURL: values?.baseurl,
        logo: values?.logo,
      })
    );

    setAvailableFilters([
      {
        displayName: "Name",
        name: "id",
        type: "multiselect",
        suffix: "contains",
        options: businessListOptionArray,
      },
      {
        displayName: "Keyword",
        name: "name",
        type: "text",
        suffix: "contains",
      },
      {
        displayName: "Status",
        name: "status",
        type: "radio",
        suffix: ":",
        options: [
          { label: "Active", value: "1", displayValue: "Active" },
          { label: "Inactive", value: "2", displayValue: "Inactive" },
        ],
      },
    ]);
    setIsLoading(false);
  }, [bunchBusinessList]); // Add businessList as a dependency

  const handleSaveForm = (e: any) => {
    e.preventDefault();
    const event = new Event("submit", {
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  };

  return (
    <div className="container">
      <PageHeader
        title="BUSINESS"
        buttonText="ADD NEW"
        onButtonClick={handleModalOpen}
        backPath="/setting"
      />
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={
            selectedBusiness
              ? `Edit Business: ${formatEntityName(selectedBusiness)}`
              : "Add New Business"
          }
          width="80vw"
          closeOnOutsideClick={false}
          footer={
            <div className="d-flex justify-content-end w-100">
              <Button type="light" onClick={handleModalClose} className="me-2">
                Cancel
              </Button>
              <Button
                type="primary"
                isDisabled={
                  !formState.isDirty ||
                  //  !formState.isValid ||
                  formState.isLoading.loading
                }
                onClick={(e) => handleSaveForm(e)}
              >
                Save
              </Button>
            </div>
          }
        >
          <AddBusinessForm
            onClose={handleModalClose}
            onFormStateChange={setFormState}
          />
        </Modal>
      )}{" "}
      <Table
        columns={headers}
        isLoading={loading}
        totalCount={count}
        appliedFilters={filters}
        availableFilters={availableFilters}
        handleSetFilters={handleSetFilters}
        filters={filters}
        isSetinParams={false}
        getListMedia={getListBusiness}
        apiCallDelayTimer={0}
        isClearFilter={isClearFilter}
        downloadCsv={true}
      >
        {businessList.length > 0 ? (
          [...businessList]
            .sort((a: any, b: any) => {
              const keyword =
                typeof filters?.name === "string"
                  ? filters.name.trim().toLowerCase()
                  : "";
              if (!keyword) return 0;
              const aName = (a.name || "").toLowerCase();
              const bName = (b.name || "").toLowerCase();
              const score = (n: string) =>
                n === keyword
                  ? 0
                  : n.startsWith(keyword)
                    ? 1
                    : n.includes(keyword)
                      ? 2
                      : 3;
              return score(aName) - score(bName);
            })
            .map((item: any) => (
              <TableTR key={item.id} rowKey={item.id}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <Avatar
                      text={item.name}
                      size={30}
                      logo={item?.logo}
                      baseURL={item?.baseurl}
                    />
                    <span>{formatEntityName(item)}</span>
                  </div>
                </td>
                <td valign="middle">
                  <div className="d-flex align-items-center">
                    {formatTimestampToAgo(item?.ct)}
                  </div>
                </td>
                <td valign="middle">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: archivingId === item.id ? 0.5 : 1,
                      cursor: archivingId === item.id ? "wait" : "pointer",
                    }}
                    onClick={() => handleArchiveToggle(item)}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 20,
                        borderRadius: 10,
                        background: item.archive === 1 ? "#ccc" : "#2563eb",
                        position: "relative",
                        transition: "background 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 3,
                          left: item.archive === 1 ? 3 : 19,
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          background: "#fff",
                          transition: "left 0.2s",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 13,
                        color: item.archive === 1 ? "#888" : "#2563eb",
                        fontWeight: 500,
                      }}
                    >
                      {item.archive === 1 ? "Inactive" : "Active"}
                    </span>
                  </div>
                </td>
                <td valign="middle">
                  <div className="d-flex align-items-center">
                    <span
                      className="btn btn-outline-secondary"
                      title="Edit"
                      onClick={() => handleEdit(item)}
                    >
                      <Edit size={16} />
                    </span>
                  </div>
                </td>
              </TableTR>
            ))
        ) : (
          <TableTR rowKey="no-content">
            <td colSpan={headers.length}>
              <NoContentFound message="No Business Found" fill />
            </td>
          </TableTR>
        )}
      </Table>
    </div>
  );
};

const mapState = (state: RootState) => ({
  businessList: state.constants.businessList.list,
  count: state.constants.businessList.count,
  organizationList: state.constants.organizationList.list,
  loading: state.constants.businessList.isLoading,
  currentBusiness: state.constants.currentBusiness,

  isClearFilter: state.media.isClearFilter,
  bunchBusinessList: state.constants.bunchBusinessList.list,
});

const mapDispatch = {
  getListBusiness,
  getListOrganization,
  setCurrentBusiness,
  setBusinessList,
  getBusiness,
  archiveBusiness,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(BusinessList);
